import { ActionTree } from 'vuex'
import { AuthState } from './types'
import { RootState } from '../types'
import store from '@/store'
import { ThingsHelpers } from '@/helpers/mainExports.js'
import {
  magentaOAuth,
  connectionService,
  ConnectionWSShared,
  PERSON_FETCH_URI,
  wampUtils
} from '@leapsight/styleguide'
import router from '@/router'
import { createEntityApiDB } from '@/api/indexDB/entity'
import { TABLE } from '@/constants/tableNames'
import { serializationThingsDB } from '@/api/indexDB/helpers'
import { i18n } from '@leapsight/portal'
import { SignalListener } from '@/helpers/signals'

const MAX_LIMIT_TO_SHOW_THINGS = 1000

export const actions: ActionTree<AuthState, RootState> = {
  checkTokenIsActive() {
    return new Promise((resolve, reject) => {
      //Existe token?
      const token = localStorage.getItem('ACCESS_TOKEN')

      //Valido que este activo
      if (token) {
        magentaOAuth
          .active()
          .then(response => {
            resolve(response)
          })
          .catch(err => {
            reject()
          })
      } else {
        resolve(false)
      }
    })
  },

  async logout({ state, commit }) {
    try {
      const res = await magentaOAuth.logout()
      if (res.status === 200) {
        const connection = state.connection
        if (Boolean(connection)) ConnectionWSShared.closeConnection()
        commit('logout')
        router.push({ name: 'login' }).then(() => {
          window.location.reload()
        })
      }
    } catch (e) {
      console.log(e)
    } finally {
      store.commit('setDisconnectDialogValue', false, { root: true })
    }
  },
  getThingParams({ state, commit, dispatch }, thingID) {
    thingID = String(thingID)
    return new Promise((resolve, reject) => {
      //Open the connection
      state.connection || dispatch('getConnection')

      const kwArgs = connectionService.createkwArgs()
      kwArgs.types = ['mrn:thing:vehicle', 'mrn:thing:flex']
      const options = {
        timeout: 5000
      }

      /*  When the connection is open we clean the map State to delete outdated data, then set in the
       *  store that the connection is Active (boolean).
       *  We recive all the thing the user is authorized through a WAMP call, with the response we make
       *  a WAMP subscription to each THING we recive to get the updates
       */

      const session = state.connection.session

      store.dispatch('subscriptionState/cleanSubscriptionState')

      dispatch('personFetch', session)

      store.dispatch('notificationsState/subscribe', session)
      store.dispatch('notificationsState/setUnreadNotifications')
      commit('wsConnectionActive', true)
      const idThing = [thingID]
      if (idThing) {
        session.call('com.magenta.thing.fetch', idThing, kwArgs, options).then(
          async res => {
            const thingSelected = await store.dispatch(
              'mapState/addLocationsThingSelect',
              res.args[0]
            )

            await store.dispatch('trailState/setThingTrail', thingSelected)
            await store.dispatch('uiState/openTrailView')
            resolve(thingSelected)
          },
          wampError => {
            store.dispatch('uiState/openSnackBar', {
              msg: 'No se pudo recuperar información.'
            })
            reject(wampError)
          }
        )
      }
    })
  },
  getConnection({ commit }, { fnConnOnOpen, fnConnOnClose }) {
    const conn = ConnectionWSShared.getInstance({
      fnConnOnOpen,
      fnConnOnClose
    }).connection
    commit('setConnection', conn)
  },
  async establishConnection(
    { state, commit, dispatch, rootGetters },
    addSignal
  ) {
    let resolve: (p: any) => void
    let reject: (p: any) => void
    const promise = new Promise((_resolve, _reject) => {
      resolve = _resolve
      reject = _reject
    })
    store.dispatch('uiState/setMainGridLoading', {
      establishLoading: true
    })
    state.connection?.session &&
      (await store.dispatch('subscriptionState/unsubscribeThingsTopicUpdate'))

    const fnConnOnOpen = async session => {
      try {
        store.dispatch('mapState/destroyMarkerClusterOnMap')
        store.dispatch('mapState/cleanMapState')
        store.dispatch('subscriptionState/cleanSubscriptionState')

        store.dispatch('notificationsState/subscribe', session)
        store.dispatch('notificationsState/setUnreadNotifications')
        commit('wsConnectionActive', true)
        const personId = localStorage.getItem('user_id') ?? ''
        const thingApiDB = createEntityApiDB(TABLE.THINGS)
        const thingsFilteredDBByUser = await ThingsHelpers.callThingFromLocalDB(
          { entityApiDB: thingApiDB }
        )

        let things = []

        if (thingsFilteredDBByUser.length > 0) {
          let elementInDB: any = {}
          let results: any = []
          let chunk: any = []
          const batchSize = 40

          // se invocan los fetch por lotes para no sobrecargar el servidor
          for (
            let index = 0;
            index < thingsFilteredDBByUser.length;
            index += batchSize
          ) {
            chunk = thingsFilteredDBByUser.slice(index, index + batchSize)

            results = await Promise.all(
              chunk.map(item => {
                return ThingsHelpers.fetchThing(item.id)
              })
            )

            try {
              for (const result of results) {
                elementInDB = thingsFilteredDBByUser.find(item => {
                  return item.id === result.args[0].id
                })
                if (
                  result.args[0].metadata.reported_state.location.timestamp >
                  elementInDB.metadata.reported_state.location.timestamp
                ) {
                  elementInDB.metadata.reported_state.location.timestamp =
                    result.args[0].metadata.reported_state.location.timestamp
                  elementInDB.metadata.reported_state.signal_sensors =
                    result.args[0].metadata.reported_state.signal_sensors
                  elementInDB.metadata.reported_state.speed =
                    result.args[0].metadata.reported_state.speed
                  elementInDB.reported_state.signal_sensors =
                    result.args[0].reported_state.signal_sensors
                  elementInDB.reported_state.location.coordinates =
                    result.args[0].reported_state.location.coordinates
                  elementInDB.reported_state.speed =
                    result.args[0].reported_state.speed
                  elementInDB.reported_state.heading =
                    result.args[0].reported_state.heading
                  elementInDB.reported_state.ibutton =
                    result.args[0].reported_state.ibutton
                  elementInDB.reported_state.fuel_cut_off =
                    result.args[0].reported_state.fuel_cut_off
                  elementInDB.reported_state.battery_level =
                    result.args[0].reported_state.battery_level
                  elementInDB.reported_state.contact_on =
                    result.args[0].reported_state.contact_on
                  if (elementInDB.reported_state.ignition_status) {
                    elementInDB.reported_state.ignition_status =
                      result.args[0].reported_state.ignition_status
                  }
                  elementInDB.type = result.args[0].type
                  elementInDB.is_alias = result.args[0].is_alias
                  if (elementInDB.telemetry) {
                    elementInDB.telemetry = result.args[0].telemetry
                  }
                  const data = serializationThingsDB(elementInDB)
                  await thingApiDB.update({
                    id: elementInDB.id,
                    entity: data
                  })
                }
              }
            } catch (error) {
              console.log(error)
            }
          }

          things = thingsFilteredDBByUser
          addSignal(things)
          dispatch('getThingsList', {
            page: 1,
            things: [],
            total: 0,
            session: session,
            addSignal: addSignal,
            filterIsApplied: true,
            limit: 200
          }).then(() => {
            store.dispatch('uiState/setMainGridLoading', {
              establishLoading: false
            })
          })
          store.commit('setFilterMustBeApplied', {
            filterMustBeApplied: false
          })
        } else {
          const tagsApiDB = createEntityApiDB(TABLE.TAGS)
          const tagsFilteredDB = await tagsApiDB.list()

          const tagsFilteredDBByUser = tagsFilteredDB.filter(thingFilteredDB =>
            thingFilteredDB.personsId.some(pId => pId === personId)
          )
          if (
            thingsFilteredDBByUser.length === 0 &&
            tagsFilteredDBByUser.length > 0
          ) {
            things = []
            dispatch('getThingsList', {
              page: 1,
              things: [],
              total: 0,
              session: session,
              addSignal: addSignal,
              limit: 200
            }).then(() => {
              store.dispatch('uiState/setMainGridLoading', {
                establishLoading: false
              })
            })
          } else {
            things = await dispatch('getThingsList', {
              page: 1,
              things: [],
              total: 0,
              session: session,
              addSignal: addSignal,
              limit: 200
            })
            store.dispatch('uiState/setMainGridLoading', {
              establishLoading: false
            })
            if (things.length > MAX_LIMIT_TO_SHOW_THINGS) {
              store.commit('setFilterMustBeApplied', {
                filterMustBeApplied: true
              })
              store.dispatch('uiState/setMainGridLoading', {
                loadingThingsSubscription: false
              })
            }
          }
        }

        if (!store.state.filterMustBeApplied) {
          ThingsHelpers.subscribeThingsToBeUpdated({
            things,
            updateCallback: addSignal
          }).finally(() => {
            store.dispatch('uiState/setMainGridLoading', {
              loadingThingsSubscription: false
            })
          })

          store.dispatch('uiState/setAmountOfThing', things.length)

          store.dispatch(
            'uiState/toggleVisibleNumerator',
            rootGetters['uiState/showCluster']
          )

          dispatch('subscribeUserUpdated', () => {
            wampUtils.TokenServices.clearToken()
            commit(
              'setDisconnectDialogMessage',
              i18n.t(
                'general.disconnectDialogComponent.message_modified_profile'
              ),
              { root: true }
            )
            store.commit('setDisconnectDialogValue', true, {
              root: true
            })
            setTimeout(() => dispatch('logout'), 10000)
          })

          dispatch('subscribeDeviceDeleted', response => {
            if (
              response[0].info.identifier ===
              localStorage.getItem('DEVICE_IDENTIFIER')
            ) {
              wampUtils.TokenServices.clearToken()
              commit(
                'setDisconnectDialogMessage',
                i18n.t(
                  'general.disconnectDialogComponent.message_deleted_device'
                ),
                { root: true }
              )
              commit('setDisconnectDialogValue', true, { root: true })
              setTimeout(() => dispatch('logout'), 10000)
            }
          })
          resolve(true)
        }

        return promise
      } catch (e) {
        console.error(e)
        reject(e)
      }
    }

    const fnConnOnClose = details => {
      store.state.connectionRetryCount++

      if (store.state.connectionRetryCount > 10) {
        const snackbar = {
          msg:
            'La conexión con la plataforma se ha perdido. Por favor, actualiza la página (F5) para continuar.'
        }
        store.dispatch('uiState/openSnackBar', snackbar)
      }
      console.error('Reason disconnect: ', details)
      const { reason } = details
      if (reason === null) {
        store.dispatch('mapState/cleanMapState')
        store.dispatch('subscriptionState/cleanSubscriptionState')
      }

      if (wampUtils.isReasonToDisconnect({ reason })) {
        wampUtils.TokenServices.clearToken()
        commit(
          'setDisconnectDialogMessage',
          i18n.t('general.disconnectDialogComponent.message_modified_profile'),
          { root: true }
        )
        store.commit('setDisconnectDialogValue', true, { root: true })
        setTimeout(() => dispatch('logout'), 10000)
        return
      }

      try {
        if (
          reason === 'wamp.error.no_such_principal' ||
          reason === 'wamp.error.authentication_failed'
        ) {
          magentaOAuth.refreshToken().then(res => {
            if (res.status === 200)
              dispatch('establishConnection', SignalListener.addSignal)
            else dispatch('logout')
          })
        }
      } catch (e) {
        console.error(e)
      }
    }

    if (Boolean(state.connection?.session?.isOpen))
      fnConnOnOpen(state.connection.session)
    else {
      dispatch('getConnection', { fnConnOnOpen, fnConnOnClose })
    }
  },
  getThingsList(
    { dispatch },
    { page, things, total, session, addSignal, filterIsApplied, limit } = {
      page: 1,
      things: [],
      total: 0,
      session: {},
      filterIsApplied: false,
      limit: 200
    }
  ) {
    return new Promise((resolve, reject) => {
      ThingsHelpers.thingFind({ session, page, limit })
        .then(response => {
          if (total === 0) {
            total = response?.kwargs?.total || 0
          }

          if (response?.kwargs?.total > 999) {
            store.commit('setTotalAmountOfThings', {
              totalAmountOfThings: response.kwargs.total
            })
            limit = 1000
            things = things.concat(response.args[0])
            const dataFiltered = things.filter(entity => {
              const liveTracking =
                entity.config?.services?.['mrn:service:live_tracking']
                  ?.status !== 'inactive' &&
                entity.config?.service_plan?.status !== 'inactive'
              const hasLocation = Boolean(entity.reported_state.location)
              return liveTracking && hasLocation
            })

            if (things.length >= total || response.args[0].length === 0) {
              resolve(dataFiltered)
              store.dispatch('setThingsFromResponse', {
                thingsFromResponse: dataFiltered
              })
            } else {
              page++
              dispatch('getThingsList', { page, things, total, session, limit })
                .then(response => {
                  resolve(response)
                })
                .catch(error => {
                  reject(error)
                })
            }
          } else {
            things = things.concat(response.args[0])
            const dataFiltered = things.filter(entity => {
              const liveTracking =
                entity.config?.services?.['mrn:service:live_tracking']
                  ?.status !== 'inactive' &&
                entity.config?.service_plan?.status !== 'inactive'
              const hasLocation = Boolean(entity.reported_state.location)
              return liveTracking && hasLocation
            })

            if (!filterIsApplied) {
              addSignal(dataFiltered)
            }

            if (things.length >= total || response.args[0].length === 0) {
              resolve(dataFiltered)
              store.commit('setTotalAmountOfThings', {
                totalAmountOfThings: response.kwargs.total
              })
              store.dispatch('setThingsFromResponse', {
                thingsFromResponse: dataFiltered
              })
            } else {
              page++
              dispatch('getThingsList', {
                page,
                things,
                total,
                session,
                addSignal,
                filterIsApplied,
                limit
              })
                .then(response => {
                  resolve(response)
                })
                .catch(error => {
                  reject(error)
                })
            }
          }
        })
        .catch(error => {
          const errorText = ThingsHelpers.getErrorMessageByCode(error.error)
          const snackbar = {
            msg: errorText
          }
          store.dispatch('uiState/openSnackBar', snackbar)
          reject(error)
        })
    })
  },
  personFetch({ commit }, session) {
    return new Promise((resolve, reject) => {
      const kwArgs = connectionService.createkwArgs()
      const userId = connectionService.getBusinessUserId()
      session
        .call(PERSON_FETCH_URI, [userId], kwArgs, {})
        .then(res => {
          // Se verifica que el usuario logueado sea Administrador
          const userGroups = res.kwargs.security.groups
          let userIsAdmin = false
          let userIsMonitoring = false
          if (userGroups.includes('business_account_admin')) {
            userIsAdmin = true
            userIsMonitoring = false
          } else if (userGroups.includes('business_account_user')) {
            userIsMonitoring = true
            userIsAdmin = false
          }
          const user = {
            id: res.args[0].id,
            name: res.args[0].first_name,
            lastName: res.args[0].last_name,
            username: res.args[0].roles['mrn:party_role:user'].username,
            avatarImage: res.args[0].photo,
            isAdmin: userIsAdmin,
            isMonitoring: userIsMonitoring
          }
          commit('setUserData', user)
          const lsUser = {
            name: user.name,
            last_name: user.lastName
          }
          localStorage.setItem('user', JSON.stringify(lsUser))
          resolve(user)
        })
        .catch(err => {
          console.log(err)
          reject(err)
        })
    })
  },
  subscribeUserUpdated({ getters }, callback) {
    const connection = getters['connection']
    const accountId = wampUtils.getAccountUUID(connectionService.getAccountId())
    const personId = wampUtils.getPersonUUID(
      connectionService.getBusinessUserId()
    )
    return new Promise<void>(resolve => {
      const topic = `com.magenta.account.${accountId}.user.${personId}.updated`
      connection.session
        .subscribe(topic, callback, { match: 'exact' })
        .then(() => {
          resolve()
        })
    })
  },
  subscribeDeviceDeleted({ getters }, callback) {
    const connection = getters['connection']
    return new Promise<void>(resolve => {
      const topic = 'com.magenta.device.deleted'
      connection.session
        .subscribe(topic, callback, { match: 'exact' })
        .then(() => {
          resolve()
        })
    })
  }
}
